<template>
  <Block type="secondary">
    <div class="flex flex-col">
      <div class="flex items-center gap-2 mb-4">
        <img src="/img/hlp.png" alt="" class="h-7 w-7" />
        <span class="text-white">
          <a
            :href="hlpLink"
            target="_blank"
            rel="noopener noreferrer"
          >
            HLP
          </a>
        </span>
      </div>
      <SwapTab v-model="activeSwapTab" />
      <div
        class="flex flex-col items-center justify-center gap-3 border-b-2 border-block-2 pb-4 mt-4"
      >
        <Input
          :label="`${
            activeSwapTab == 'buy' ? 'Deposit' : 'Sell'
          }: ${amountToTransactFormatted} ${sourceFieldSymbol} ($${amountToTransactUSDFormatted})`"
          show-balance
          compact
          :icon="
            activeSwapTab == 'buy' ? '/img/eth.png' : '/img/hlp.png'
          "
          v-model="amountToTransactNoDecimals"
          :max="maxAmountToTransact"
          :balance="
            activeSwapTab == 'buy'
              ? userETHBalanceFixed
              : userHLPBalanceFixed
          "
        />
        <IconSwap
          style="cursor: pointer"
          @click="
            activeSwapTab == 'buy'
              ? (activeSwapTab = 'sell')
              : (activeSwapTab = 'buy')
          "
        />
        <Input
          :label="`Receive: ${amountToTransactFormatted} ${targetFieldSymbol} ($${amountToTransactUSDFormatted})`"
          show-balance
          compact
          :icon="
            activeSwapTab == 'buy' ? '/img/hlp.png' : '/img/eth.png'
          "
          v-model="amountToTransactNoDecimals"
          :max="maxAmountToTransact"
          :balance="
            activeSwapTab == 'buy'
              ? userHLPBalanceFixed
              : userETHBalanceFixed
          "
        />
        <Button
          v-if="!isConnected"
          text="Connect Wallet"
          secondary
          small
          full
          class="mt-2"
          @click="init"
        />
        <Button
          v-else
          :text="activeSwapTab == 'buy' ? 'Buy HLP' : 'Sell HLP'"
          secondary
          small
          full
          @click="transactHLP"
        />
      </div>
      <div class="flex flex-col mt-4 gap-3">
        <div class="flex items-center gap-2">
          <img src="/img/hlp.png" alt="" class="h-7 w-7" />
          <span class="text-white">HLP Rewards</span>
        </div>
        <RewardText
          title="Total HLP"
          :value="`${totalHLPFormatted} HLP ($${totalHLPUSDFormatted})`"
        />
        <RewardText
          title="Total Earned Fees"
          :value="`${ethRewardsExtTotalRewardsFormatted} ${nativeCurrencySymbol} ($${ethRewardsExtTotalRewardsUSDFormatted})`"
        />
        <RewardText
          title="Your HLP"
          :value="`${userHLPBalanceFormatted} HLP ($${userHLPBalanceUSDFormatted})`"
        />
        <RewardText
          title="Your Total Earned Fees"
          :value="`${ethRewardsExtUserTotalFormatted} ${nativeCurrencySymbol} ($${ethRewardsExtUserTotalUSDFormatted})`"
        />
        <RewardText
          title="Your Unclaimed Fees"
          :value="`${ethRewardsExtUserUnclaimedFormatted} ${nativeCurrencySymbol} ($${ethRewardsExtUserUnclaimedUSDFormatted})`"
        />
        <Button
          v-if="!isConnected"
          text="Connect Wallet"
          secondary
          small
          @click="init"
        />
        <Button
          v-else-if="isConnected && ethRewardsExtUserHasUnclaimed"
          text="Claim Rewards"
          secondary
          small
          @click="claimRewards"
        />
      </div>
      <Loading v-if="isProcessing" />
    </div>
  </Block>
</template>

<script>
import BigNumber from "bignumber.js";
import { mapActions, mapGetters } from "vuex";
import Block from "../global/Block.vue";
import Input from "../global/Input.vue";
import IconSwap from "../icons/IconSwap.vue";
import Button from "../global/Button.vue";
import Loading from "../global/Loading.vue";
import SwapTab from "./SwapTab.vue";
import RewardText from "../rewards/RewardText.vue";
import { formatDynamicDecimals } from "../../factories/Utils";
export default {
  name: "Swap",
  components: {
    Block,
    Input,
    IconSwap,
    Button,
    Loading,
    SwapTab,
    RewardText,
  },

  watch: {
    activeSwapTab() {
      this.amountToTransactNoDecimals = this.maxAmountToTransact;
    },
  },

  data() {
    return {
      activeSwapTab: "buy",
      amountToTransactNoDecimals: 0,
      isProcessing: false,
    };
  },

  computed: {
    ...mapGetters([
      "activeNetwork",
      "isConnected",
      "ethPriceUSDFixed",
      "ethRewardsExtTotalRewardsFixed",
      "ethRewardsExtTotalRewardsFormatted",
      "ethRewardsExtTotalRewardsUSDFormatted",
      "ethRewardsExtUserUnclaimedFixed",
      "ethRewardsExtUserHasUnclaimed",
      "ethRewardsExtUserUnclaimedFormatted",
      "ethRewardsExtUserUnclaimedUSDFormatted",
      "ethRewardsExtUserTotalFixed",
      "ethRewardsExtUserTotalFormatted",
      "ethRewardsExtUserTotalUSDFormatted",
      "nativeCurrencySymbol",
      "totalHLPFormatted",
      "totalHLPUSDFormatted",
      "userETHBalanceFixed",
      "userHLPBalanceFixed",
      "userHLPBalanceFormatted",
      "userHLPBalanceUSDFormatted",
    ]),

    hlpLink() {
      return (
        (this.activeNetwork &&
          this.activeNetwork.contracts.hlp &&
          `${this.activeNetwork.explorer_url}/token/${this.activeNetwork.contracts.hlp}`) ||
        ""
      );
    },

    maxAmountToTransact() {
      return this.activeSwapTab === "buy"
        ? this.userETHBalanceFixed
        : this.userHLPBalanceFixed;
    },

    sourceFieldSymbol() {
      return this.activeSwapTab === "buy"
        ? this.nativeCurrencySymbol
        : "HLP";
    },

    targetFieldSymbol() {
      return this.activeSwapTab === "buy"
        ? "HLP"
        : this.nativeCurrencySymbol;
    },

    amountToTransactFormatted() {
      return formatDynamicDecimals(
        new BigNumber(this.amountToTransactNoDecimals)
      );
    },

    amountToTransactUSDFormatted() {
      return new BigNumber(this.amountToTransactNoDecimals)
        .times(this.ethPriceUSDFixed)
        .toFormat(2);
    },
  },
  methods: {
    ...mapActions([
      "init",
      "ethRewardsExtClaim",
      "buyHLP",
      "sellHLP",
    ]),

    async transactHLP() {
      try {
        this.isProcessing = true;
        const amountNoDec = new BigNumber(
          this.amountToTransactNoDecimals
        );
        if (amountNoDec.eq(0)) {
          return this.$notify({
            type: "error",
            text: `Please enter a valid amount.`,
          });
        }
        const amountDec = amountNoDec
          .times(new BigNumber(10).pow(18))
          .toFixed(0);
        if (this.activeSwapTab === "buy") {
          await this.buyHLP(amountDec);
        } else {
          await this.sellHLP(amountDec);
        }
      } catch (err) {
        this.$notify({ type: "error", text: err.message });
      } finally {
        this.isProcessing = false;
      }
    },

    async claimRewards() {
      try {
        this.isProcessing = true;
        if (!this.ethRewardsExtUserHasUnclaimed) {
          return this.$notify({
            type: "error",
            text: `You don't have any unclaimed rewards.`,
          });
        }
        await this.ethRewardsExtClaim();
      } catch (err) {
        this.$notify({ type: "error", text: err.message });
      } finally {
        this.isProcessing = false;
      }
    },
  },
};
</script>
